/* global $ */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

function VendorDetails() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [vendor, setVendor] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [loadingVendor, setLoadingVendor] = useState(true);
    const [loadingContacts, setLoadingContacts] = useState(true);
    const [activeTab, setActiveTab] = useState('details');

    // Add Contact Modal state
    const [showAddContact, setShowAddContact] = useState(false);
    const [savingContact, setSavingContact] = useState(false);
    const [contactForm, setContactForm] = useState({
        name: '', email: '', phone: '', designation: '', notes: ''
    });
    const [contactErrors, setContactErrors] = useState({});

    // Contact table state
    const [contactSearch, setContactSearch] = useState('');
    const [contactPage, setContactPage] = useState(1);
    const contactsPerPage = 10;

    // ─── Fetch Vendor ─────────────────────────────────────────────────────────────

    const fetchVendor = async () => {
        try {
            setLoadingVendor(true);
            const res = await axiosInstance.get(`/api/vendors/${id}`);
            setVendor(res.data.data || res.data);
        } catch (error) {
            console.error("Error fetching vendor:", error);
            Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to fetch vendor details' });
        } finally {
            setLoadingVendor(false);
        }
    };

    // ─── Fetch Contacts ───────────────────────────────────────────────────────────

    const fetchContacts = async () => {
        try {
            setLoadingContacts(true);
            const res = await axiosInstance.get(`/api/vendorsContact?vendor_id=${id}`);
            setContacts(res.data.data || res.data);
        } catch (error) {
            console.error("Error fetching contacts:", error);
            setContacts([]);
        } finally {
            setLoadingContacts(false);
        }
    };

    useEffect(() => {
        fetchVendor();
        fetchContacts();
    }, [id]);

    // ─── Contact Form ─────────────────────────────────────────────────────────────

    const handleContactChange = (e) => {
        setContactForm({ ...contactForm, [e.target.name]: e.target.value });
    };

    const validateContact = () => {
        const errs = {};
        if (!contactForm.name) errs.name = "Name is required";
        if (!contactForm.phone) errs.phone = "Phone is required";
        setContactErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSaveContact = async (e) => {
        e.preventDefault();
        if (!validateContact()) return;
        setSavingContact(true);

        try {
            const created_by = localStorage.getItem("user_id");
            await axiosInstance.post("/api/vendorsContact", {
                ...contactForm,
                vendor_id: id,
                isactive: true,
                created_by
            });

            Swal.fire({ icon: 'success', title: 'Saved!', text: 'Contact added successfully', timer: 1500, showConfirmButton: false });
            setContactForm({ name: '', email: '', phone: '', designation: '', notes: '' });
            setContactErrors({});
            setShowAddContact(false);
            fetchContacts();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error!', text: error.response?.data?.message || 'Failed to save contact' });
        } finally {
            setSavingContact(false);
        }
    };

    const handleDeleteContact = async (contactId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This contact will be deleted.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/vendorsContact/${contactId}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Contact removed.', timer: 1500, showConfirmButton: false });
                fetchContacts();
            } catch (error) {
                Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to delete contact' });
            }
        }
    };

    // ─── Filtered contacts ────────────────────────────────────────────────────────

    const filteredContacts = contacts.filter(c =>
        c.name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        c.email?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        c.phone?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        c.designation?.toLowerCase().includes(contactSearch.toLowerCase())
    );

    const totalContactPages = Math.ceil(filteredContacts.length / contactsPerPage);
    const contactStartIdx = (contactPage - 1) * contactsPerPage;
    const paginatedContacts = filteredContacts.slice(contactStartIdx, contactStartIdx + contactsPerPage);

    // ─── Helper ───────────────────────────────────────────────────────────────────

    const InfoRow = ({ label, value }) => (
        <div className="col-md-6 mb-3">
            <label className="text-muted small mb-1 d-block">{label}</label>
            <span className="fw-medium">{value || <span className="text-muted">—</span>}</span>
        </div>
    );

    // ─── Render ───────────────────────────────────────────────────────────────────

    if (loadingVendor) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!vendor) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="alert alert-danger">Vendor not found.</div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-2">
                                <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => navigate('/vendor')}>
                                    <i className="fa fa-arrow-left me-1"></i> Back
                                </button>
                                <h5 className="mb-0">{vendor.name}</h5>
                                <span className={`badge ms-2 ${vendor.isactive ? 'bg-success' : 'bg-danger'}`}>
                                    {vendor.isactive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={() => navigate('/add-vendor', { state: { vendor, isEdit: true } })}
                            >
                                <i className="fa fa-edit me-1"></i> Edit Vendor
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="card mb-0">
                        <div className="card-body">
                            <ul className="nav nav-tabs mb-4">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('details')}
                                    >
                                        <i className="fa fa-building me-1"></i> Vendor Details
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'contacts' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('contacts')}
                                    >
                                        <i className="fa fa-users me-1"></i> Associated Contacts
                                        <span className="badge bg-secondary ms-2">{contacts.length}</span>
                                    </button>
                                </li>
                            </ul>

                            {/* ── Vendor Details Tab ── */}
                            {activeTab === 'details' && (
                                <div>
                                    <h6 className="text-primary border-bottom pb-2 mb-3">Basic Information</h6>
                                    <div className="row">
                                        <InfoRow label="Vendor Name" value={vendor.name} />
                                        <InfoRow label="Email" value={vendor.email} />
                                        <InfoRow label="Phone" value={vendor.phone} />
                                        <InfoRow label="Alternate Phone" value={vendor.alternate_phone} />
                                        <InfoRow label="Website" value={
                                            vendor.website
                                                ? <a href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`} target="_blank" rel="noopener noreferrer">{vendor.website}</a>
                                                : null
                                        } />
                                        <InfoRow label="GST Number" value={vendor.gst_number} />
                                        <InfoRow label="PAN Number" value={vendor.pan_number} />
                                        <InfoRow label="Status" value={
                                            <span className={`badge ${vendor.isactive ? 'bg-success' : 'bg-danger'}`}>
                                                {vendor.isactive ? 'Active' : 'Inactive'}
                                            </span>
                                        } />
                                    </div>

                                    <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Address</h6>
                                    <div className="row">
                                        <InfoRow label="Address Line 1" value={vendor.address_line1} />
                                        <InfoRow label="Address Line 2" value={vendor.address_line2} />
                                        <InfoRow label="City" value={vendor.city?.name || vendor.city_id} />
                                        <InfoRow label="State" value={vendor.state?.name || vendor.state_id} />
                                        <InfoRow label="Country" value={vendor.country?.name || vendor.country_id} />
                                        <InfoRow label="Pincode" value={vendor.pincode} />
                                        {vendor.latitude && vendor.longitude && (
                                            <InfoRow label="Coordinates" value={`${vendor.latitude}, ${vendor.longitude}`} />
                                        )}
                                    </div>

                                    {vendor.notes && (
                                        <>
                                            <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Notes</h6>
                                            <p className="text-muted">{vendor.notes}</p>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* ── Contacts Tab ── */}
                            {activeTab === 'contacts' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="text-primary mb-0">Associated Contacts</h6>
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={() => { setShowAddContact(true); setContactErrors({}); }}
                                        >
                                            <i className="fa fa-plus-circle me-1"></i> Add Contact
                                        </button>
                                    </div>

                                    {/* Search */}
                                    <div className="d-flex justify-content-end mb-3">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            style={{ width: '220px' }}
                                            placeholder="Search contacts..."
                                            value={contactSearch}
                                            onChange={(e) => { setContactSearch(e.target.value); setContactPage(1); }}
                                        />
                                    </div>

                                    {/* Add Contact Form (inline) */}
                                    {showAddContact && (
                                        <div className="card border mb-4">
                                            <div className="card-header d-flex justify-content-between align-items-center py-2">
                                                <span className="fw-semibold">New Contact</span>
                                                <button
                                                    type="button"
                                                    className="btn-close btn-sm"
                                                    onClick={() => { setShowAddContact(false); setContactErrors({}); }}
                                                />
                                            </div>
                                            <div className="card-body">
                                                <form onSubmit={handleSaveContact}>
                                                    <div className="row">
                                                        <div className="col-md-3 mb-3">
                                                            <label>Name *</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                name="name"
                                                                value={contactForm.name}
                                                                onChange={handleContactChange}
                                                            />
                                                            {contactErrors.name && <small className="text-danger">{contactErrors.name}</small>}
                                                        </div>
                                                        <div className="col-md-3 mb-3">
                                                            <label>Email</label>
                                                            <input
                                                                type="email"
                                                                className="form-control"
                                                                name="email"
                                                                value={contactForm.email}
                                                                onChange={handleContactChange}
                                                            />
                                                        </div>
                                                        <div className="col-md-3 mb-3">
                                                            <label>Phone *</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                name="phone"
                                                                value={contactForm.phone}
                                                                onChange={handleContactChange}
                                                            />
                                                            {contactErrors.phone && <small className="text-danger">{contactErrors.phone}</small>}
                                                        </div>
                                                        <div className="col-md-3 mb-3">
                                                            <label>Designation</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                name="designation"
                                                                value={contactForm.designation}
                                                                onChange={handleContactChange}
                                                            />
                                                        </div>
                                                        <div className="col-md-12 mb-3">
                                                            <label>Notes</label>
                                                            <textarea
                                                                className="form-control"
                                                                name="notes"
                                                                rows={2}
                                                                value={contactForm.notes}
                                                                onChange={handleContactChange}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="text-end">
                                                        <button type="button" className="btn btn-secondary btn-sm me-2" onClick={() => setShowAddContact(false)}>
                                                            Cancel
                                                        </button>
                                                        <button type="submit" className="btn btn-primary btn-sm" disabled={savingContact}>
                                                            {savingContact ? 'Saving...' : 'Save Contact'}
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    )}

                                    {/* Contacts Table */}
                                    {loadingContacts ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-center table-hover">
                                                <thead className="thead-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Name</th>
                                                        <th>Email</th>
                                                        <th>Phone</th>
                                                        <th>Designation</th>
                                                        <th>Notes</th>
                                                        <th>Status</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedContacts.length > 0 ? (
                                                        paginatedContacts.map((contact, idx) => (
                                                            <tr key={contact.id}>
                                                                <td>{contactStartIdx + idx + 1}</td>
                                                                <td>
                                                                    <span
                                                                        style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                                                        onClick={() => navigate(`/vendor-contact-details/${contact.id}`, { state: { from: 'vendor-details', vendor_id: id } })}
                                                                        title="View contact details"
                                                                    >
                                                                        {contact.name}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {contact.email
                                                                        ? <a href={`mailto:${contact.email}`} className="text-primary">{contact.email}</a>
                                                                        : 'N/A'}
                                                                </td>
                                                                <td>{contact.phone || 'N/A'}</td>
                                                                <td>{contact.designation || 'N/A'}</td>
                                                                <td>
                                                                    {contact.notes
                                                                        ? <span title={contact.notes}>{contact.notes.length > 40 ? contact.notes.slice(0, 40) + '…' : contact.notes}</span>
                                                                        : 'N/A'}
                                                                </td>
                                                                <td>
                                                                    <span className={`badge ${contact.isactive ? 'bg-success' : 'bg-danger'}`}>
                                                                        {contact.isactive ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </td>
                                                                <td className="d-flex align-items-center">
                                                                    <a
                                                                        className="btn-action-icon bg-primary-light me-2"
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => navigate(`/vendor-contact-details/${contact.id}`, { state: { from: 'vendor-details', vendor_id: id } })}
                                                                        title="View Details"
                                                                    >
                                                                        <i className="fa fa-eye"></i>
                                                                    </a>
                                                                    <a
                                                                        className="btn-action-icon bg-danger-light"
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => handleDeleteContact(contact.id)}
                                                                        title="Delete"
                                                                    >
                                                                        <i className="fa fa-trash-alt"></i>
                                                                    </a>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="8" className="text-center text-muted py-4">
                                                                {contactSearch ? 'No matching contacts found' : 'No contacts added yet'}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {/* Contact Pagination */}
                                    {filteredContacts.length > contactsPerPage && (
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div>
                                                Showing {contactStartIdx + 1} to {Math.min(contactStartIdx + contactsPerPage, filteredContacts.length)} of {filteredContacts.length}
                                            </div>
                                            <nav>
                                                <ul className="pagination mb-0">
                                                    <li className={`page-item ${contactPage === 1 ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => setContactPage(contactPage - 1)}>Previous</button>
                                                    </li>
                                                    {Array.from({ length: totalContactPages }, (_, i) => i + 1).map(n => (
                                                        <li key={n} className={`page-item ${n === contactPage ? 'active' : ''}`}>
                                                            <button className="page-link" onClick={() => setContactPage(n)}>{n}</button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${contactPage === totalContactPages ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => setContactPage(contactPage + 1)}>Next</button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                    </div>

                </div>
                <Footer />
            </div>
        </div>
    );
}

export default VendorDetails;